import React, { useState, useEffect } from "react";
import {
  getStatementByAccount,
  getStatementToExport,
  sendStatementByEmail,
  getAgedBalancesByAccount,
  getBankingDetailsByPracticeNumbers,
  decryptField,
  getReceiptByNumber,
  sendInvoiceByEmail,
  getCreditNoteByNumber,
} from "../../afinanceapi";
import { getUserPracticeById, getPracticeById } from "../../api";
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Container,
  Modal,
  Spinner,
} from "react-bootstrap";
import BulkExportModal from "./BulkExportModal";
import CreateReceiptModal from "../Accounts/Receipt";
import CreditNote from "./CreditNote";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./AccountStatement.css";
import handleExportPDF from "./ExportStatementPDF";
import EmailStatementModal from "./EmailStatement";
import { ExportPDFReceipt } from "./ExportReceiptPDF";

export default function AccountStatements({ account }) {
  const [accountId, setAccountId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [practiceIds, setPracticeIds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [practice, setPractice] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [receiptAccount, setReceiptAccount] = useState(null);
  const [creditNoteAccount, setCreditNoteAccount] = useState(null);
  const [receiptInvoiceNumber, setReceiptInvoiceNumber] = useState(null);
  const [statementBlob, setStatementBlob] = useState(null);
  const [agedBalances, setAgedBalances] = useState([]);
  const [statementData, setStatementData] = useState(null);
  const [banking, setBanking] = useState(null);
  const [selectedReceiptEntry, setSelectedReceiptEntry] = useState(null);
  const [showReceiptActionModal, setShowReceiptActionModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState("");
  const [showStatementEmailModal, setShowStatementEmailModal] = useState(false);
  const [showReceiptEmailModal, setShowReceiptEmailModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showBulkExport, setShowBulkExport] = useState(false);
  const [unallocatedBalance, setUnallocatedBalance] = useState(0);
  const [userRole, setUserRole] = useState(false);
  const [docEmailBlob, setDocEmailBlob] = useState(null);     // PDF for receipt or credit-note
const [docEmailType, setDocEmailType] = useState("receipt"); // "receipt" | "creditNote"
const [docEmailData, setDocEmailData] = useState(null);      // emailData to send to backend

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchAccountData() {
      if (account && account._id) {
        setAccountId(account._id);
        const token = sessionStorage.getItem("user");
        const user = jwtDecode(token);
        const practiceData = await getUserPracticeById(user._id);
        setPractice(practiceData);
        setPracticeIds(user.practice || []);
        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
          ? [user.role]
          : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];

        const allRoles = [
          ...new Set([...rolesFromRole, ...rolesFromPermissions]),
        ];
        const allowed = new Set(["Finance", "SuperAdmin"]);
        const hasAllowedRole = allRoles.some((r) => allowed.has(r));

        setUserRole(!!hasAllowedRole);

        const todayDate = new Date();
        const formattedEndDate = todayDate.toISOString().split("T")[0];

        const oneMonthBefore = new Date();
        oneMonthBefore.setMonth(todayDate.getMonth() - 1);
        const formattedStartDate = oneMonthBefore.toISOString().split("T")[0];
        setStartDate(formattedStartDate);
        setEndDate(formattedEndDate);
      }
    }
    fetchAccountData();
  }, [account]);

  useEffect(() => {
    if (accountId && startDate && endDate && practiceIds.length > 0) {
      fetchData();
    }
  }, [accountId, startDate, endDate, practiceIds]);

  function formatDate(isoString) {
    return isoString ? isoString.split("T")[0] : "";
  }

  async function fetchData() {
    if (!accountId || !startDate || !endDate || practiceIds.length === 0)
      return;

    try {
      const data = await getStatementByAccount(
        accountId,
        startDate,
        endDate,
        practiceIds
      );
      const { invoices = [], openingBalance = 0, closingBalance = 0 } = data;

      const transactions = [];
      // Opening Balance
      transactions.push({
        date: formatDate(startDate),
        description: "Opening Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: openingBalance,
      });

      // Process invoices and receipts
      invoices.forEach((inv) => {
        const invoiceAmount = parseFloat(inv.invoiceTotalAmount || 0);
        const balance = parseFloat(inv.balance || 0);

        transactions.push({
          invoiceId: inv._id,
          invoice: inv,
          date: formatDate(inv.invoiceDate),
          description: `Invoice-${inv.invoiceNumber}`,
          status: inv.status,
          invoiceAmount,
          receiptFromPatient: inv.patientTotal,
          receiptFromMedicalAid: inv.medicalAidTotal,
          balance: inv.runningBalance,
        });

        inv.receipts.forEach((r, idx) => {
          const isLastReceipt = idx === inv.receipts.length - 1;

          transactions.push({
            receiptNumber:
              r.receiptNumber || r.creditNoteNumber || r.refundNumber,
            receiptDate: r.receiptDate || r.creditNoteDate || r.refundDate,
            paymentAmount:
              r.receiptAmount || r.creditNoteAmount || r.refundAmount || 0,
            receiptReference:
              r.receiptReference ||
              r.creditNoteReference ||
              r.refundReference ||
              "",
            practiceNumber: inv.practiceNumber,
            date: "",
            description: `${r.receiptNumber} (${r.receiptDate})`, // now “INV-12345 (2025-06-11)”
            status: "",
            paymentSource: r.paymentSource,
            receiptFromPatient: ["Patient", "patient"].includes(r.paymentSource)
              ? r.receiptAmount
              : r.creditNoteAmount || 0,
            receiptFromMedicalAid: ["Medical Aid", "medicalAid"].includes(
              r.paymentSource
            )
              ? r.receiptAmount
              : r.creditNoteAmount || 0,

            //    balance: isLastReceipt ? parseFloat(inv.balance) : undefined,
          });
        });
      });

      // Closing Balance
      transactions.push({
        date: formatDate(endDate),
        description: "Closing Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: closingBalance,
      });

      setTransactions(transactions);
      setBalance(closingBalance);

      const agedBalances = await getAgedBalancesByAccount(accountId);
      setAgedBalances(agedBalances.agedBalances);
      setUnallocatedBalance(agedBalances.unallocatedTotal);

      const practiceNumbers = [
        ...new Set(invoices.map((inv) => inv.practiceNumber).filter(Boolean)),
      ];

      // Fetch and decrypt banking details
      const rawBankingData = await getBankingDetailsByPracticeNumbers(
        practiceNumbers
      );

      const decryptedBankingData = await Promise.all(
        rawBankingData.map(async (entry) => {
          const decryptedAccountNumber = entry.bank?.accountNumber
            ? await decryptField(entry.bank.accountNumber)
            : "";
          const decryptedAccountName = entry.bank?.accountName
            ? await decryptField(entry.bank.accountName)
            : "";

          return {
            ...entry,
            bank: {
              ...entry.bank,
              accountNumber: decryptedAccountNumber,
              accountName: decryptedAccountName,
            },
          };
        })
      );

      setBanking(decryptedBankingData);
    } catch (error) {
      console.error(
        "❌ Error fetching statements:",
        error.response?.data || error.message
      );
    }
  }

  function buildReceiptEmailData({ receipt, account, practice }) {
  const practiceNumber = practice?.practiceNumber || "";
  const patientFirstName = account?.accountFullNames || "";
  const patientLastName  = account?.accountLastName  || "";

  return {
    practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
    practiceName:  practice?.practiceName  || "",
    practiceEmail: practice?.practiceEmail || "",
    practicePhone: practice?.practicePhoneNumber || "",
    practiceNumber,
    logoURL:       practice?.logoURL || "",

    patientFirstName,
    patientLastName,
    patientName: [patientFirstName, patientLastName].filter(Boolean).join(" "),

    receiptNumber: receipt?.receiptNumber || "",
    receiptDate:   receipt?.receiptDate   || "",
  };
}

function buildCreditNoteEmailData({ creditNote, account, practice }) {
  const practiceNumber = practice?.practiceNumber || "";
  const patientFirstName = account?.accountFullNames || "";
  const patientLastName  = account?.accountLastName  || "";

  return {
    practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
    practiceName:  practice?.practiceName  || "",
    practiceEmail: practice?.practiceEmail || "",
    practicePhone: practice?.practicePhoneNumber || "",
    practiceNumber,
    logoURL:       practice?.logoURL || "",

    patientFirstName,
    patientLastName,
    patientName: [patientFirstName, patientLastName].filter(Boolean).join(" "),

    creditNoteNumber: creditNote?.creditNoteNumber || "",
    creditNoteDate:   creditNote?.creditNoteDate   || "",
  };
}


  async function exportPDF() {
    try {
      let balances = agedBalances;

      if (!balances || Object.keys(balances).length === 0) {
        balances = await getAgedBalancesByAccount(accountId);
        setAgedBalances(balances);
      }

      const data = await getStatementToExport(
        accountId,
        startDate,
        endDate,
        practiceIds
      );
      console.log("Statement data for PDF export:", data);
      data.agedBalances = balances;
      data.banking = banking;
      const download = handleExportPDF(data);
      await download();
    } catch (error) {
      console.error("❌ Error exporting statement PDF:", error);
      alert("Failed to export PDF. Please try again.");
    }
  }

  // Apply similar checks to your email function
  async function prepareEmailStatement() {
    try {
      let balances = agedBalances;

      if (!balances || Object.keys(balances).length === 0) {
        balances = await getAgedBalancesByAccount(accountId);
        setAgedBalances(balances);
      }
      const data = await getStatementToExport(
        accountId,
        startDate,
        endDate,
        practiceIds
      );

      data.agedBalances = balances;
      data.banking = banking;

      const pdfGenerator = handleExportPDF(data);
      const blob = await pdfGenerator({ asBlob: true });

      setStatementData(data);
      setStatementBlob(blob);
      setShowStatementEmailModal(true);
    } catch (error) {
      console.error("❌ Failed to generate statement data:", error);
      alert("Failed to prepare email statement. Please try again.");
    }
  }

  const handleCreateInvoice = (e) => {
    e.stopPropagation();
    navigate(`/invoices/1/${account._id}`);
  };

  const handleCreateReceipt = (e) => {
    setReceiptAccount(account);
    setShowReceiptModal(true); // ✅ Show receipt modal
  };
  const handleCreditNote = (e) => {
    setCreditNoteAccount(account);
    setShowCreditNoteModal(true); // ✅ Show receipt modal
  };

  async function handleDownloadClicked() {
    let cnDataImported;
    let receiptDataImported;

    if (!selectedReceiptEntry) return;

    const r = selectedReceiptEntry;

    if (r.receiptNumber.includes("CN-")) {
      cnDataImported = await getCreditNoteByNumber(r.receiptNumber);
    } else {
      receiptDataImported = await getReceiptByNumber(r.receiptNumber);
    }

    const practiceData = await getPracticeById(
      receiptDataImported?.receiptPracticeNumber ||
        cnDataImported?.creditNotePracticeNumber
    );

    // rebuild the `receiptData` shape your ExportPDFReceipt expects:
    const receiptData = {
      receiptNumber:
        receiptDataImported?.receiptNumber || cnDataImported?.creditNoteNumber,
      receiptDate:
        receiptDataImported?.receiptDate || cnDataImported?.creditNoteDate,
      receiptTotalAmount:
        receiptDataImported?.receiptTotalAmount ||
        cnDataImported?.creditNoteTotalAmount,
      receiptInvoices:
        receiptDataImported?.receiptInvoices ||
        cnDataImported?.creditNoteInvoiceLines,
      receiptPaymentMethod:
        receiptDataImported?.receiptMethod ||
        cnDataImported?.creditNotePaymentMethod,
      receiptReference:
        receiptDataImported?.receiptInvoices?.[0]?.receiptReference ||
        cnDataImported?.creditNoteReference,
      practice: practiceData,
      account,
    };
    await ExportPDFReceipt(receiptData);
    setShowReceiptActionModal(false);
  }

 const handleSendDocEmail = () => {
  if (!docEmailBlob || !emailToSend) return;

  // snapshot everything before we clear state
  const blob = docEmailBlob;
  const edata = docEmailData;
  const dtype = docEmailType; // "receipt" | "creditNote"
  const email = emailToSend;
  const acctId = account?._id;

  // close + reset immediately (no spinner needed)
  setShowReceiptEmailModal(false);
  setDocEmailBlob(null);
  setDocEmailData(null);
  setEmailToSend("");

  // fire-and-forget — do NOT await
  sendInvoiceByEmail({
    email,
    pdfBlob: blob,
    type: dtype,
    accountId: acctId,
    emailData: edata,
  })
    .then(() => {
      // optional: toast("Email is being sent")
      // real-time status will arrive via your socket notification
    })
    .catch((error) => {
      console.error("Failed to send email:", error);
      // optional: toast.error("Failed to send email")
    });
  };



  async function handleSendEmail() {
  if (!selectedReceiptEntry) return;

  try {
    const r = selectedReceiptEntry;
    const isCreditNote = r.receiptNumber?.includes("CN-");

    let receiptDataImported = null;
    let creditNoteDataImported = null;

    if (isCreditNote) {
      creditNoteDataImported = await getCreditNoteByNumber(r.receiptNumber);
    } else {
      receiptDataImported = await getReceiptByNumber(r.receiptNumber);
    }

    const practiceNumber =
      receiptDataImported?.receiptPracticeNumber ||
      creditNoteDataImported?.creditNotePracticeNumber;

    const practiceData = await getPracticeById(practiceNumber);

    // shape for PDF generator (ExportPDFReceipt supports both)
    const receiptData = {
      receiptNumber:
        receiptDataImported?.receiptNumber || creditNoteDataImported?.creditNoteNumber,
      receiptDate:
        receiptDataImported?.receiptDate    || creditNoteDataImported?.creditNoteDate,
      receiptTotalAmount:
        receiptDataImported?.receiptTotalAmount ||
        creditNoteDataImported?.creditNoteTotalAmount ||
        0,
      receiptInvoices:
        receiptDataImported?.receiptInvoices ||
        creditNoteDataImported?.creditNoteInvoiceLines ||
        [],
      receiptPaymentMethod:
        receiptDataImported?.receiptMethod ||
        creditNoteDataImported?.creditNotePaymentMethod ||
        "",
      receiptReference:
        receiptDataImported?.receiptInvoices?.[0]?.receiptReference ||
        creditNoteDataImported?.creditNoteReference ||
        "",
      practice: practiceData,
      account,
    };

    // build PDF
    const blob = await ExportPDFReceipt(receiptData, { asBlob: true });

    // build emailData for templates
    const emailVars = isCreditNote
      ? buildCreditNoteEmailData({
          creditNote: {
            creditNoteNumber: receiptData.receiptNumber,
            creditNoteDate: receiptData.receiptDate,
          },
          account,
          practice: practiceData,
        })
      : buildReceiptEmailData({
          receipt: {
            receiptNumber: receiptData.receiptNumber,
            receiptDate: receiptData.receiptDate,
          },
          account,
          practice: practiceData,
        });

    // stash & open modal
    setDocEmailBlob(blob);
    setDocEmailType(isCreditNote ? "creditNote" : "receipt");
    setDocEmailData(emailVars);
    setEmailToSend(account.accountEmail || "");
    setShowReceiptEmailModal(true);
    setShowReceiptActionModal(false);
  } catch (error) {
    console.error("Failed to prepare receipt/credit note email:", error);
    alert("Failed to prepare document for email. Please try again.");
  }
}

  const canNavigate = userRole;

  return (
    <div>
      <Container fluid className="mb-2 px-4">
        {" "}
        {/* <-- Consistent horizontal padding */}
        <Row className="align-items-center justify-content-between mb-2">
          <Col md={4}>
            <h5 className="text-primary mb-0">Account Statement</h5>
          </Col>

          <Col md={8} className="d-flex justify-content-end gap-3">
            {userRole && (
              <>
                <Button
                  variant="primary"
                  onClick={handleCreateInvoice}
                  className="w-25 d-flex align-items-center justify-content-center gap-2"
                  style={{ minWidth: "130px" }}
                >
                  <i class="fa fa-file-invoice-dollar"></i>
                  Create Invoice
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateReceipt}
                  className="w-25 d-flex align-items-center justify-content-center gap-2"
                  style={{ minWidth: "130px" }}
                >
                  <i class="fa fa-receipt"></i>
                  Receipt
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreditNote}
                  className="w-25 d-flex align-items-center justify-content-center gap-2"
                  style={{ minWidth: "130px" }}
                >
                  <i class="fa fa-undo"></i>
                  Credit Note
                </Button>
              </>
            )}
            <Button
              variant="outline-secondary"
              onClick={exportPDF}
              className="w-25 d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: "130px" }}
              disabled={!agedBalances || !banking}
            >
              <i class="fa fa-download"></i>
              Download PDF
            </Button>
            <Button
              variant="outline-secondary"
              onClick={prepareEmailStatement}
              className="w-25 d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: "130px" }}
              disabled={!agedBalances || !banking}
            >
              <i class="fa-regular fa-envelope"></i>
              Email Statement
            </Button>
            <Button
              variant="outline-secondary"
              className="w-25 d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: "130px" }}
              onClick={() => setShowBulkExport(true)}
            >
              <i className="fa-solid fa-file-export me-2" />
              Bulk Export
            </Button>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Col>

          <Col md={10}>
            <Table bordered hover className="mt-1">
              <thead className="table-primary text-center text-primary">
                <tr>
                  <th style={{ width: "10%" }} className="text-primary">
                    Date
                  </th>
                  <th style={{ width: "25%" }} className="text-primary">
                    Description
                  </th>
                  <th style={{ width: "10%" }} className="text-primary">
                    Status
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Invoice | Receipt
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Patient
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Medical Aid
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((entry, index) => {
                  // ——— Computed once per entry ———
                  const isPending = entry.status === "Pending";
                  const isLastInGroup =
                    index < transactions.length - 1 &&
                    (!!transactions[index + 1].invoiceId ||
                      transactions[index + 1].description ===
                        "Closing Balance");

                  const isReceiptEntry =
                    entry.description?.includes("Receipt") ||
                    ((entry.description?.includes("RCT-") ||
                      entry.description?.includes("CN-")) &&
                      !entry.invoiceId &&
                      entry.description !== "Opening Balance" &&
                      entry.description !== "Closing Balance");

                  const isRefundEntry = entry.description?.includes("RF-");

                  // ——— Render ———
                  return (
                    <tr
                      key={`entry-${index}`}
                      onClick={() => {
                        if (!canNavigate) return; // gate everything
                        if (isReceiptEntry) {
                          setSelectedReceiptEntry({
                            receiptNumber: entry.receiptNumber,
                          });
                          setShowReceiptActionModal(true);
                        } else if (entry.invoice) {
                          const specialStatuses = ["Draft", "Pending"];
                          const path = specialStatuses.includes(entry.status)
                            ? `/invoices/${entry.invoice._id}/${account._id}`
                            : `/invoices/posted/${entry.invoice._id}`;
                          navigate(
                            path,
                            specialStatuses.includes(entry.status)
                              ? { state: { account } }
                              : undefined
                          );
                        }
                      }}
                      style={{
                        cursor:
                          canNavigate && (isReceiptEntry || entry.invoice)
                            ? "pointer"
                            : "default",
                      }}
                      title={
                        canNavigate
                          ? isReceiptEntry || entry.invoice
                            ? "Open details"
                            : ""
                          : "Insufficient permissions"
                      }
                    >
                      {/* amounts stay exactly as-is */}

                      <td>{entry.date || ""}</td>

                      <td>
                        {isReceiptEntry || isRefundEntry ? (
                          <span className="ms-3">{entry.description}</span>
                        ) : isPending ? (
                          <span>{entry.description}</span>
                        ) : (
                          <strong>{entry.description}</strong>
                        )}
                      </td>

                      <td>
                        {isPending ? (
                          <em>{entry.status}</em>
                        ) : (
                          <strong>{entry.status}</strong>
                        )}
                      </td>

                      <td className="text-center">
                        {isRefundEntry
                          ? `R ${parseFloat(entry.paymentAmount || 0).toFixed(
                              2
                            )}`
                          : entry.invoiceAmount
                          ? `R ${entry.invoiceAmount.toFixed(2)}`
                          : ""}
                      </td>

                      <td className="text-center">
                        {isReceiptEntry && entry.receiptFromPatient
                          ? `(R ${parseFloat(entry.receiptFromPatient).toFixed(
                              2
                            )})`
                          : ""}
                      </td>

                      <td className="text-center">
                        {isReceiptEntry && entry.receiptFromMedicalAid
                          ? `(R ${parseFloat(
                              entry.receiptFromMedicalAid
                            ).toFixed(2)})`
                          : ""}
                      </td>

                      <td className={`text-end${isPending ? "" : " fw-bold"}`}>
                        {entry.balance != null
                          ? `R ${entry.balance.toFixed(2)}`
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <div className="d-flex justify-content-end mt-4">
              <div style={{ width: "60%" }}>
                {" "}
                {/* Adjust this width as needed */}
                <Table bordered size="sm">
                  <thead className="table-primary text-center text-primary">
                    <tr>
                      <th className="text-primary"></th>
                      <th className="text-primary">Outstanding</th>
                      <th className="text-primary">30 Days</th>
                      <th className="text-primary">60 Days</th>
                      <th className="text-primary">90 Days</th>
                      <th className="text-primary">120+ Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-bold">Patient</td>
                      <td className="text-end text-danger fw-bold">
                        R{" "}
                        {(
                          (agedBalances.days30?.patient || 0) +
                          (agedBalances.days60?.patient || 0) +
                          (agedBalances.days90?.patient || 0) +
                          (agedBalances.days120plus?.patient || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days30?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days60?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days90?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days120plus?.patient.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Medical Aid</td>
                      <td className="text-end text-danger fw-bold">
                        R{" "}
                        {(
                          (agedBalances.days30?.medicalAid || 0) +
                          (agedBalances.days60?.medicalAid || 0) +
                          (agedBalances.days90?.medicalAid || 0) +
                          (agedBalances.days120plus?.medicalAid || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days30?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days60?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days90?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days120plus?.medicalAid.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Unallocated Funds</td>
                      <td className="fw-bold text-success text-end">
                        R {unallocatedBalance.toFixed(2)}
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
                <Modal
                  show={showReceiptActionModal}
                  onHide={() => setShowReceiptActionModal(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Receipt Options</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>Would you like to download or email this receipt?</p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="primary" onClick={handleDownloadClicked}>
                      Download
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleSendEmail}
                    >
                      Email
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowReceiptActionModal(false)}
                    >
                      Cancel
                    </Button>
                  </Modal.Footer>
                </Modal>
                <Modal
                  show={showReceiptEmailModal}
                  onHide={() => setShowReceiptEmailModal(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Email Receipt</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address:</Form.Label>
                      <Form.Control
                        type="email"
                        value={emailToSend}
                        onChange={(e) => setEmailToSend(e.target.value)}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                    <p className="text-muted">
                      The receipt will be sent as a PDF attachment to the email
                      address above.
                    </p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="primary"
                      onClick={handleSendDocEmail}
                      disabled={isSendingEmail}
                    >
                      {isSendingEmail ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />{" "}
                          Sending…
                        </>
                      ) : (
                        "Send Email"
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowReceiptEmailModal(false)}
                    >
                      Cancel
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <BulkExportModal
        show={showBulkExport}
        onHide={() => setShowBulkExport(false)}
        accountId={accountId}
        // onGenerate={handleGenerateExport}
      />
      {showCreditNoteModal && creditNoteAccount && (
        <CreditNote
          show={showCreditNoteModal}
          onHide={() => setShowCreditNoteModal(false)}
          account={creditNoteAccount}
          invoiceNumber={receiptInvoiceNumber}
        />
      )}

      {showReceiptModal && receiptAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={receiptAccount}
          invoiceNumber={receiptInvoiceNumber}
        />
      )}

      {showStatementEmailModal && statementBlob && (
        <EmailStatementModal
          show={showStatementEmailModal}
          onHide={() => setShowStatementEmailModal(false)}
          defaultEmail={account.accountEmail}
          statementData={statementData}
          pdfBlob={statementBlob}
          sendStatementByEmail={sendStatementByEmail}
        />
      )}
    </div>
  );
}
